const PENDING_LOGS_KEY = "shred-log-pending-logs";
const COMPLETED_SESSIONS_KEY = "shred-log-completed-sessions";

export interface PendingLog {
  mesocycleId: string;
  weekNumber: number;
  dayOfWeek: number;
  exerciseIndex: number;
  setIndex: number;
  reps: number;
  weight?: number;
  timestamp: string;
}

export interface CompletedSession {
  mesocycleId: string;
  weekNumber: number;
  dayOfWeek: number;
  completedAt: string;
  logs: PendingLog[];
}

export const localStorageService = {
  pendingLogs: {
    getAll(): PendingLog[] {
      const data = localStorage.getItem(PENDING_LOGS_KEY);
      return data ? JSON.parse(data) : [];
    },

    getForSession(
      mesocycleId: string,
      weekNumber: number,
      dayOfWeek: number
    ): PendingLog[] {
      return this.getAll().filter(
        (log) =>
          log.mesocycleId === mesocycleId &&
          log.weekNumber === weekNumber &&
          log.dayOfWeek === dayOfWeek
      );
    },

    save(log: PendingLog): void {
      const logs = this.getAll();
      const existingIndex = logs.findIndex(
        (l) =>
          l.mesocycleId === log.mesocycleId &&
          l.weekNumber === log.weekNumber &&
          l.dayOfWeek === log.dayOfWeek &&
          l.exerciseIndex === log.exerciseIndex &&
          l.setIndex === log.setIndex
      );

      if (existingIndex >= 0) {
        logs[existingIndex] = log;
      } else {
        logs.push(log);
      }

      localStorage.setItem(PENDING_LOGS_KEY, JSON.stringify(logs));
    },

    clearForSession(
      mesocycleId: string,
      weekNumber: number,
      dayOfWeek: number
    ): void {
      const logs = this.getAll().filter(
        (log) =>
          !(
            log.mesocycleId === mesocycleId &&
            log.weekNumber === weekNumber &&
            log.dayOfWeek === dayOfWeek
          )
      );
      localStorage.setItem(PENDING_LOGS_KEY, JSON.stringify(logs));
    },

    clear(): void {
      localStorage.removeItem(PENDING_LOGS_KEY);
    },
  },

  completedSessions: {
    getAll(): CompletedSession[] {
      const data = localStorage.getItem(COMPLETED_SESSIONS_KEY);
      return data ? JSON.parse(data) : [];
    },

    isCompleted(
      mesocycleId: string,
      weekNumber: number,
      dayOfWeek: number
    ): boolean {
      return this.getAll().some(
        (session) =>
          session.mesocycleId === mesocycleId &&
          session.weekNumber === weekNumber &&
          session.dayOfWeek === dayOfWeek
      );
    },

    markCompleted(
      mesocycleId: string,
      weekNumber: number,
      dayOfWeek: number,
      logs: PendingLog[]
    ): void {
      const sessions = this.getAll();
      const session: CompletedSession = {
        mesocycleId,
        weekNumber,
        dayOfWeek,
        completedAt: new Date().toISOString(),
        logs,
      };
      sessions.push(session);
      localStorage.setItem(COMPLETED_SESSIONS_KEY, JSON.stringify(sessions));
    },

    getSession(
      mesocycleId: string,
      weekNumber: number,
      dayOfWeek: number
    ): CompletedSession | undefined {
      return this.getAll().find(
        (session) =>
          session.mesocycleId === mesocycleId &&
          session.weekNumber === weekNumber &&
          session.dayOfWeek === dayOfWeek
      );
    },
  },
};
